import { juarezIsoDate, unclassifiedNuCents } from "./periods";
import type { FinancialSnapshot } from "./types";

export const YIELD_FUND_ID = "nu-yield-available";
export const YIELD_FUND_LABEL = "Rendimientos para anualidades";

interface YieldAvailability {
  /** Saldo actual del fondo, leído dentro de la misma transacción. */
  availableBeforeCents: number;
  /** Sólo en la primera configuración: dinero histórico que sigue libre dentro de Nu. */
  openingAvailableCents?: number;
}

export function applyYieldUpdate(
  snapshot: FinancialSnapshot,
  amountCents: number,
  updatedAt = new Date().toISOString(),
  availability?: YieldAvailability,
): FinancialSnapshot {
  const amount = amountCents;
  if (amount < 0) throw new Error("El rendimiento no puede ser negativo.");
  if (!Number.isSafeInteger(amount)) throw new Error("El rendimiento debe expresarse en centavos enteros.");
  const delta = amount - snapshot.yieldCents;
  const nuBalanceCents = snapshot.accounts[0]?.balanceCents ?? 0;
  if (!Number.isSafeInteger(nuBalanceCents + delta) || nuBalanceCents + delta < 0) {
    throw new Error("La actualización dejaría el saldo de Nu por debajo de cero.");
  }
  let fundDeltaCents = 0;
  let initializedAt = snapshot.yieldAllocationInitializedAt;
  if (availability) {
    const before = availability.availableBeforeCents;
    if (!Number.isSafeInteger(before) || before < 0) throw new Error("El saldo disponible de rendimientos es inválido.");
    if (initializedAt) {
      if (availability.openingAvailableCents !== undefined) throw new Error("El saldo inicial de rendimientos ya fue configurado.");
      if (before + delta < 0) throw new Error("No se puede corregir el acumulado a la baja porque parte de esos rendimientos ya fueron utilizados.");
      fundDeltaCents = delta;
    } else {
      const opening = availability.openingAvailableCents;
      if (!Number.isSafeInteger(opening) || opening === undefined || opening < 0) {
        throw new Error("Indica cuánto de los rendimientos anteriores sigue disponible.");
      }
      if (before !== 0) throw new Error("El fondo de rendimientos ya tiene dinero sin una configuración inicial verificable.");
      if (opening > amount) throw new Error("El saldo disponible no puede superar el rendimiento acumulado informado por Nu.");
      if (opening > unclassifiedNuCents(snapshot) + delta) {
        throw new Error("No hay suficiente dinero sin clasificar en Nu para reservar ese saldo inicial.");
      }
      fundDeltaCents = opening;
      initializedAt = updatedAt;
    }
  } else if (initializedAt) {
    throw new Error("Actualiza Banculem para seguir registrando rendimientos disponibles.");
  }
  const temporaryContributionCents = snapshot.temporaryContributionCents + fundDeltaCents;
  if (!Number.isSafeInteger(temporaryContributionCents) || temporaryContributionCents < 0) {
    throw new Error("El total de fondos disponibles quedaría inválido.");
  }
  return {
    ...snapshot,
    yieldCents: amount,
    ...(initializedAt ? { yieldAllocationInitializedAt: initializedAt } : {}),
    yieldUpdatedAt: updatedAt,
    yieldUpdatedDateJuarez: juarezIsoDate(new Date(updatedAt)),
    temporaryContributionCents,
    accounts: snapshot.accounts.map((account, index) => index === 0
      ? { ...account, balanceCents: account.balanceCents + delta }
      : account),
  };
}
